import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMainCategories } from "../../store/slices/mainCategorySlice";
import { fetchCategoriesByMainCategory } from "../../store/slices/categorySlice";
import { fetchSubCategories } from "../../store/slices/subCategorySlice";
import { TRANSPARENT_PLACEHOLDER } from "../../utils/constants";
import { motion } from "framer-motion";

// Standard hover effect helper
const hoverStyles = `
    .nav-item-container:hover .mega-menu { 
        display: flex !important; 
        animation: fadeIn 0.15s ease-out; 
    }
    .nav-item-container:hover .nav-text { 
        color: var(--color-primary) !important; 
    }
    .nav-item-container:hover .nav-indicator { 
        opacity: 1 !important; 
        transform: scaleX(1) !important; 
    }
    
    .mega-menu-column h4 { 
        color: var(--color-primary); 
        font-size: 14px; 
        margin-bottom: 12px; 
        font-weight: 700;
        text-transform: capitalize;
    }
    .mega-menu-item { 
        font-size: 14px; 
        margin: 6px 0; 
        color: #282c3f; 
        cursor: pointer;
        transition: 0.1s;
        text-decoration: none;
        display: block;
    }
    .mega-menu-item:hover { 
        font-weight: 700;
        color: var(--color-primary);
    }
    
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(5px); }
        to { opacity: 1; transform: translateY(0); }
    }

    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

    .category-nav-wrapper {
        position: sticky !important;
        top: 128px !important;
        z-index: 98 !important;
        background-color: #ffffff !important;
    }

    /* Mobile: chip-style category row */
    @media (max-width: 900px) {
        .mega-menu { display: none !important; }
        .nav-indicator { display: none !important; }
        
        .category-nav-wrapper {
            top: 136px !important;
            height: 60px !important;
            background: #ffffff !important;
            border-bottom: 1px solid #f0f2f5 !important;
            display: flex !important;
            align-items: center !important;
            position: sticky !important;
            padding: 0 !important;
            z-index: 98 !important;
        }

        .nav-list-container {
            justify-content: flex-start !important;
            padding: 0 16px !important;
            gap: 10px !important;
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            align-items: center !important;
            height: 100% !important;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
        }

        .nav-item-container {
            height: auto !important;
            flex-shrink: 0 !important;
            margin: 0 !important;
            display: flex !important;
            align-items: center !important;
        }

        .nav-item-container a {
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            gap: 8px !important;
            padding: 6px 14px !important;
            height: auto !important;
            background-color: #f7f9fa !important;
            border-radius: 20px !important;
            border: 1px solid #f0f2f5 !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.02) !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .nav-item-container a div {
            width: 22px !important;
            height: 22px !important;
            border-radius: 50% !important;
            padding: 0 !important;
            background: transparent !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }

        .nav-item-container a div img {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain !important;
        }

        .nav-item-container a .nav-text {
            font-size: 12.5px !important;
            color: #4a4a4a !important;
            font-weight: 600 !important;
            text-transform: capitalize !important;
            letter-spacing: 0 !important;
            margin: 0 !important;
        }

        /* Active styling corrected class matching */
        .nav-item-container.active a {
            background: linear-gradient(135deg, #43c8ca 0%, #2fa8aa 100%) !important;
            border-color: #43c8ca !important;
            box-shadow: 0 4px 10px rgba(0,164,95,0.2) !important;
        }

        .nav-item-container.active a .nav-text {
            color: #ffffff !important;
        }

        .nav-item-container.active a div img {
            filter: brightness(0) invert(1) !important;
        }
        
        /* Premium Left & Right Fading Indicators */
        .category-nav-wrapper::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 24px;
            background: linear-gradient(to right, #ffffff, transparent);
            pointer-events: none;
            z-index: 10;
        }
        
        .category-nav-wrapper::after {
            content: '';
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 24px;
            background: linear-gradient(to left, #ffffff, transparent);
            pointer-events: none;
            z-index: 10;
        }
    }
`;

const MegaMenu = ({ children }: { children: React.ReactNode }) => (
  <div
    className="mega-menu"
    style={{
      position: "absolute",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      width: "100vw",
      backgroundColor: "white",
      boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
      padding: "40px 0",
      display: "none",
      zIndex: 1000,
      borderTop: "1px solid #f5f5f6",
      cursor: "default",
    }}
  >
    <div
      style={{
        maxWidth: "100%",
        width: "100%",
        margin: "0 auto",
        display: "flex",
        flexWrap: "wrap",
        gap: "30px",
        padding: "0 24px",
      }}
    >
      {children}
    </div>
  </div>
);

const NavItem = ({
  showImage,
  name,
  to,
  image,
  children,
  isActive,
  isScrolled,
  onMouseEnter,
}: {
  name: string;
  to: string;
  showImage?: boolean;
  image: string;
  children?: React.ReactNode;
  isActive?: boolean;
  isScrolled?: boolean;
  onMouseEnter?: () => void;
}) => {
  return (
    <div
      className={`nav-item-container${isActive ? " active" : ""}`}
      style={{
        position: "static",
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
      onMouseEnter={onMouseEnter}
    >
      <Link
        to={to}
        style={{
          textDecoration: "none",
          padding: isScrolled ? "4px 12px" : "8px 16px",
          height: "auto",
          display: "flex",
          flexDirection: isScrolled ? "row" : "column",
          alignItems: "center",
          position: "relative",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1001,
          gap: isScrolled ? "10px" : "8px",
          borderRadius: "8px",
        }}
      >
        {showImage && (
          <motion.div
            whileHover={{ y: isScrolled ? 0 : -4, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
            style={{
              width: isScrolled ? "32px" : "40px",
              height: isScrolled ? "32px" : "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isActive ? "rgba(40, 116, 240, 0.05)" : "transparent",
              borderRadius: isScrolled ? "6px" : "12px",
              padding: "2px",
            }}
          >
            <img
              src={image ? encodeURI(image) : TRANSPARENT_PLACEHOLDER}
              alt={name}
              onError={(e) => {
                (e.target as HTMLImageElement).src = TRANSPARENT_PLACEHOLDER;
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: isActive ? "none" : "grayscale(0.1) contrast(0.9)",
                transition: "0.3s",
              }}
            />
          </motion.div>
        )}

        <span
          className="nav-text"
          style={{
            fontSize: "14px",
            fontWeight: "700",
            color: isActive ? "#2874f0" : "#212121",
            whiteSpace: "nowrap",
            textAlign: "center",
            transition: "0.2s",
            fontFamily:
              'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {name}
        </span>

        <motion.div
          layoutId="nav-indicator"
          className="nav-indicator"
          initial={false}
          animate={{
            opacity: isActive ? 1 : 0,
            scaleX: isActive ? 1 : 0,
          }}
          style={{
            position: "absolute",
            bottom: "-12px",
            left: "15%",
            right: "15%",
            height: "4px",
            backgroundColor: "#2874f0",
            borderRadius: "4px 4px 0 0",
            transformOrigin: "center",
          }}
        />
      </Link>
      {children}
    </div>
  );
};

interface CategoryListProps {
  isScrolled?: boolean;
  showImage?: boolean;
}

const CategoryList: React.FC<CategoryListProps> = ({
  isScrolled,
  showImage = true,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { mainCategories, loading: mainCategoriesLoading } = useSelector(
    (state: any) => state.mainCategory,
  );
  const {
    categories,
    hoveredCategories,
    loading: categoriesLoading,
  } = useSelector((state: any) => state.category);
  const { subCategories, loading: subCategoriesLoading } = useSelector(
    (state: any) => state.subCategory,
  );
  const { products } = useSelector((state: any) => state.product);

  const fetchCategoriesOnHover = (mcId: string) => {
    dispatch(fetchCategoriesByMainCategory(mcId) as any);
    dispatch(
      fetchSubCategories({ filter: { main_category: { _id: mcId } } }) as any,
    );
  };

  useEffect(() => {
    dispatch(fetchMainCategories() as any);
    // We only fetch main categories on mount.
    // Categories for mega menu are now fetched on hover.
  }, [dispatch]);

  // ── Skeleton Components ───────────────────────────────────────────────────
  const Shimmer = () => (
    <motion.div
      initial={{ x: "-100%" }}
      animate={{ x: "100%" }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
      }}
    />
  );

  const MainCategorySkeleton = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        padding: "12px 0",
        minWidth: "80px",
      }}
    >
      <div
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "50%",
          background: "#f0f0f0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Shimmer />
      </div>
      <div
        style={{
          width: "50px",
          height: "10px",
          borderRadius: "4px",
          background: "#f0f0f0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Shimmer />
      </div>
    </div>
  );

  const MenuImage = ({ src }: { src: string }) => (
    <div style={{ paddingRight: "20px", borderRight: "1px solid #f0f0f0" }}>
      <img
        src={src || TRANSPARENT_PLACEHOLDER}
        alt="Category"
        style={{
          width: "160px",
          border: "1px solid #eee",
          borderRadius: "4px",
          marginBottom: "15px",
        }}
        onError={(e) => {
          (e.target as any).src = TRANSPARENT_PLACEHOLDER;
        }}
      />
      <div style={{ fontSize: "12px", color: "#878787", fontWeight: "600" }}>
        TRENDING COLLECTIONS
      </div>
    </div>
  );

  const FeaturedProduct = ({ product }: { product: any }) => (
    <Link
      to={`/product/${product._id}`}
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "flex",
        gap: "10px",
        alignItems: "center",
        marginBottom: "12px",
      }}
    >
      <img
        src={product.images?.[0]?.url || TRANSPARENT_PLACEHOLDER}
        alt=""
        style={{
          width: "45px",
          height: "45px",
          objectFit: "cover",
          borderRadius: "4px",
        }}
      />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: "700",
            color: "#212121",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {product.name}
        </div>
        <div style={{ fontSize: "11px", color: "#388e3c", fontWeight: "700" }}>
          ₹{product.price.toLocaleString()}
        </div>
      </div>
    </Link>
  );

  return (
    <div
      className="category-nav-wrapper"
      style={{
        backgroundColor: "#fff",
        width: "100%",
        height: isScrolled ? "50px" : (showImage ? "84px" : "50px"),
        boxShadow: isScrolled ? "0 4px 12px rgba(0,0,0,0.05)" : "none",
        display: "flex",
        alignItems: "center",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        zIndex: 99,
        borderBottom: "1px solid #f5f5f6",
      }}
    >
      <style>{hoverStyles}</style>

      <div
        className="nav-list-container no-scrollbar"
        style={{
          maxWidth: "1480px",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          gap: "48px",
          justifyContent: "flex-start",
          overflowX: "auto",
          margin: "0 auto",
        }}
      >
        {mainCategoriesLoading && mainCategories.length === 0
          ? Array(8)
            .fill(0)
            .map((_, i) => <MainCategorySkeleton key={i} />)
          : [...mainCategories]
            .sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999))
            .map((mc: any) => (
            <NavItem
              showImage={showImage}
              key={mc._id}
              image={mc.image}
              name={mc.name}
              to={`/products?main_category=${mc._id}`}
              isActive={location.pathname === "/products" && new URLSearchParams(location.search).get("main_category") === mc._id}
              isScrolled={isScrolled}
            // onMouseEnter={() => fetchCategoriesOnHover(mc._id)}
            >
              {/* <MegaMenu>
                                <MenuImage src={mc.image} />

                                <div style={{ display: 'flex', flex: 1, flexWrap: 'wrap', gap: '30px', padding: '0 20px' }}>
                                    {categoriesLoading && !hoveredCategories[mc._id] ? (
                                        <div style={{ display: 'flex', flex: 1, gap: '20px' }}>
                                            {[1, 2, 3].map(i => (
                                                <div key={i} style={{ width: '150px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <div style={{ width: '80%', height: '14px', borderRadius: '4px', background: '#f5f5f5', position: 'relative', overflow: 'hidden' }}>
                                                        <Shimmer />
                                                    </div>
                                                    {[1, 2, 3, 4].map(j => (
                                                        <div key={j} style={{ width: '60%', height: '10px', borderRadius: '4px', background: '#f5f5f5', position: 'relative', overflow: 'hidden' }}>
                                                            <Shimmer />
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        (hoveredCategories[mc._id] || categories.filter((c: any) => {
                                            const categoryMainId = c.main_category?._id || c.main_category || c.main_category_id;
                                            return categoryMainId === mc._id;
                                        }))
                                            .slice(0, 4) // Show up to 4 columns
                                            .map((cat: any) => (
                                                <div key={cat._id} className="mega-menu-column" style={{ width: '180px', textAlign: 'left' }}>
                                                    <h4>{cat.name}</h4>
                                                    {subCategoriesLoading && subCategories.length === 0 ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {[1, 2, 3, 4].map(k => (
                                                                <div key={k} style={{ width: '70%', height: '8px', borderRadius: '4px', background: '#f8f8f8', position: 'relative', overflow: 'hidden' }}>
                                                                    <Shimmer />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        subCategories
                                                            .filter((sc: any) => {
                                                                const parentCategoryId = sc.category?._id || sc.category || sc.category_id;
                                                                return parentCategoryId === cat._id;
                                                            })
                                                            .slice(0, 8)
                                                            .map((sub: any) => (
                                                                <Link
                                                                    to={`/products?subcategory=${sub._id}`}
                                                                    key={sub._id}
                                                                    className="mega-menu-item"
                                                                >
                                                                    {sub.name}
                                                                </Link>
                                                            ))
                                                    )}
                                                </div>
                                            ))
                                    )}
                                </div>

                                <div style={{ width: '220px', borderLeft: '1px solid #f0f0f0', paddingLeft: '25px' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--color-primary)', marginBottom: '15px', textTransform: 'uppercase' }}>Featured {mc.name}</div>
                                    {products
                                        .filter((p: any) => {
                                            const pMainId = p.main_category?._id || p.main_category || p.main_category_id;
                                            return pMainId === mc._id;
                                        })
                                        .slice(0, 3)
                                        .map((prod: any) => (
                                            <FeaturedProduct key={prod._id} product={prod} />
                                        ))
                                    }
                                    {products.filter((p: any) => p.main_category_id === mc._id || p.main_category === mc._id).length === 0 && (
                                        <div style={{ fontSize: '12px', color: '#878787' }}>New arrivals coming soon!</div>
                                    )}
                                </div>
                            </MegaMenu> */}
            </NavItem>
          ))}
      </div>
    </div>
  );
};

export default CategoryList;
